import React, { useEffect, useState, useRef } from 'react'
import SignatureCapture from './SignatureCapture'
import { getApartmentsByOrder } from './apartmentsApi'
import { getAttendedApartments } from './contractControlApi'
import { API_URL } from './config'
import { getToken } from './auth'
import { normalizeSupplyQuantity, getValidSupplyPayload } from './supplyUtils'
import { readOrderPestsCache } from './apartmentPestsApi'

// Ahora recibe settings como prop
export default function ApartmentsList({ order, onSelect, lang = 'en', onAllAttended, settings, onOrderCompleted, onBack }) {
  // --- Insumos por orden (OrderSupplyUsed) ---
  const [orderSupplies, setOrderSupplies] = useState([]);
  const [suppliesList, setSuppliesList] = useState([]);
  const [suppliesError, setSuppliesError] = useState('');
  const [suppliesSuccess, setSuppliesSuccess] = useState('');
  const [savingSupplies, setSavingSupplies] = useState(false);

  const normalizeSupplyId = value => {
    const text = String(value ?? '').trim();
    if (!text) return null;
    const numeric = Number(text);
    return Number.isInteger(numeric) ? numeric : text;
  };

  // Cargar lista de insumos disponibles
  useEffect(() => {
    const jwt = localStorage.getItem('jwt_token');
    fetch(`${API_URL}/supplies`, {
      headers: {
        'Authorization': jwt ? `Bearer ${jwt}` : ''
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.ok && Array.isArray(data.data)) setSuppliesList(data.data);
      });
  }, []);

  // Cargar insumos ya registrados para la orden
  useEffect(() => {
    if (!order?.id) return;
    const jwt = localStorage.getItem('jwt_token');
    fetch(`${API_URL}/orders/${order.id}/supplies-used`, {
      headers: {
        'Authorization': jwt ? `Bearer ${jwt}` : ''
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.ok && Array.isArray(data.data)) {
          setOrderSupplies(data.data.map(item => ({
            ...item,
            supplyId: normalizeSupplyId(item.supplyId ?? item.id),
            quantity: normalizeSupplyQuantity(item.quantity)
          })));
        }
      });
  }, [order?.id, suppliesSuccess]);

  // Handlers para agregar/editar insumos
  const handleOrderSupplyChange = (idx, field, value) => {
    setOrderSupplies(sups => {
      const arr = [...sups];
      const nextValue = field === 'quantity'
        ? (value === '' ? '' : String(value).trim())
        : value;
      arr[idx] = { ...arr[idx], [field]: nextValue };
      return arr;
    });
  };

  const addOrderSupply = () => {
    const existingIds = orderSupplies.map(s => normalizeSupplyId(s.supplyId));
    const available = suppliesList.find(x => !existingIds.includes(normalizeSupplyId(x.id)));
    if (!available) return;
    setOrderSupplies(sups => [...sups, { supplyId: normalizeSupplyId(available.id), quantity: 0 }]);
  };

  const removeOrderSupply = idx => setOrderSupplies(sups => sups.filter((_, i) => i !== idx));

  // Guardar insumos por orden
  const saveOrderSupplies = async () => {
    setSuppliesError('');
    setSuppliesSuccess('');
    setSavingSupplies(true);
    try {
      const jwt = localStorage.getItem('jwt_token');
      const validSupplies = getValidSupplyPayload(orderSupplies);
      if (validSupplies.length === 0) throw new Error(lang === 'en' ? 'Add at least one supply.' : 'Agrega al menos un insumo.');
      const resp = await fetch(`${API_URL}/orders/${order.id}/supplies-used`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': jwt ? `Bearer ${jwt}` : ''
        },
        body: JSON.stringify(validSupplies)
      });
      if (!resp.ok) throw new Error(lang === 'en' ? 'Error saving supplies.' : 'Error al guardar insumos.');
      setSuppliesSuccess(lang === 'en' ? 'Supplies saved.' : 'Insumos guardados.');
    } catch (e) {
      setSuppliesError(e.message || 'Error');
    } finally {
      setSavingSupplies(false);
    }
  };

  const [mostrarPendientes, setMostrarPendientes] = useState(true);

  // Estados para acciones finales
  const [showSignature, setShowSignature] = useState(false);
  const [completingOrder, setCompletingOrder] = useState(false);

  const signatureKey = `pwa_order_signature_${order?.id}`;
  const [signatureData, setSignatureData] = useState(() => {
    try { return localStorage.getItem(signatureKey) || null; } catch { return null; }
  });
  const [firmaManager, setFirmaManager] = useState(() => !!localStorage.getItem(signatureKey));

  useEffect(() => {
    if (!order?.id) return;
    const key = `pwa_order_signature_${order.id}`;
    if (signatureData) localStorage.setItem(key, signatureData);
    else localStorage.removeItem(key);
  }, [order?.id, signatureData]);

  // Definir L al inicio para evitar error de React
  const L = {
    en: {
      title: 'Apartments of',
      contracted: 'Contracted',
      attended: 'Attended',
      limit: 'Limit reached, no more apartments to do.'
    },
    es: {
      title: 'Apartamentos de',
      contracted: 'Contratados',
      attended: 'Atendidos',
      limit: 'Apartamentos contratados alcanzado.'
    }
  }[lang] || {
    title: 'Apartments of',
    contracted: 'Contracted',
    attended: 'Attended',
    limit: 'Limit reached, no more apartments can be attended.'
  };
  const [apartments, setApartments] = useState([])
  const [apartmentPests, setApartmentPests] = useState({}); // { [apartmentId]: [plagas] }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [contracted, setContracted] = useState(null)
  const [attended, setAttended] = useState(null)

  // Calcular el límite antes de cualquier uso
  const totalApartments = Array.isArray(apartments)
    ? apartments.filter(ap => ap?.id !== undefined && ap?.id !== null).length
    : 0;

  const limiteAlcanzado = attended !== null && attended >= totalApartments;

  const hydrateApartmentState = (snapshot) => {
    if (!snapshot) return;

    setApartments(Array.isArray(snapshot.apartments) ? snapshot.apartments : []);
    setContracted(snapshot.contractedUnits ?? 0);
    setAttended(snapshot.attendedCount ?? 0);
    setApartmentPests(snapshot.apartmentPests || {});
    setLoading(false);
  };

  const readOrderSnapshot = (orderId) => {
    if (!orderId) return null;
    const cacheKey = `pwa_order_apartments_${orderId}`;

    try {
      const cachedRaw = sessionStorage.getItem(cacheKey);
      if (!cachedRaw) return null;
      const cached = JSON.parse(cachedRaw);
      if (!cached || !Array.isArray(cached.apartments)) return null;
      return cached;
    } catch (e) {
      return null;
    }
  };

  const writeOrderSnapshot = (orderId, snapshot) => {
    if (!orderId || !snapshot) return;
    const cacheKey = `pwa_order_apartments_${orderId}`;

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(snapshot));
    } catch (e) {
      // Ignorar error de almacenamiento.
    }
  };

  const hasApartmentSnapshotChanged = (prev, next) => {
    if (!prev || !next) return true;

    const prevJson = JSON.stringify({
      apartments: prev.apartments || [],
      contractedUnits: prev.contractedUnits ?? 0,
      attendedCount: prev.attendedCount ?? 0,
      apartmentPests: prev.apartmentPests || {},
    });

    const nextJson = JSON.stringify({
      apartments: next.apartments || [],
      contractedUnits: next.contractedUnits ?? 0,
      attendedCount: next.attendedCount ?? 0,
      apartmentPests: next.apartmentPests || {},
    });

    return prevJson !== nextJson;
  };

  const fetchApartments = async ({ force = false } = {}) => {
    if (!order || !order.id) {
      setError(lang === 'en' ? 'No order selected or order data is invalid.' : 'No se seleccionó una orden o los datos son inválidos.');
      setLoading(false);
      return;
    }

    const orderId = order.id;
    const cached = readOrderSnapshot(orderId);

    if (cached && Array.isArray(cached.apartments) && !force) {
      hydrateApartmentState(cached);
      setApartmentPests(readOrderPestsCache(orderId) || {});
      return;
    }

    setLoading(true);
    setError('');

    try {
      const [aptsResult, attendedCount] = await Promise.all([
        getApartmentsByOrder(orderId),
        getAttendedApartments(orderId)
      ]);

      const snapshot = {
        apartments: aptsResult.apartments,
        contractedUnits: aptsResult.contractedUnits,
        attendedCount,
        apartmentPests: aptsResult.apartmentPests || {},
        savedAt: Date.now(),
      };

      const shouldUpdate = force || !cached || hasApartmentSnapshotChanged(cached, snapshot);

      if (shouldUpdate) {
        writeOrderSnapshot(orderId, snapshot);
        hydrateApartmentState(snapshot);
      } else if (cached) {
        hydrateApartmentState(cached);
      }

      setApartmentPests(aptsResult.apartmentPests || {});
    } catch (e) {
      setError(e.message);
      if (cached) {
        hydrateApartmentState(cached);
      }
      setLoading(false);
    }
  };

  // Cargar datos de la orden en montaje y volver a sincronizar cada 30s si hay cambios.
  useEffect(() => {
    if (!order?.id) return;

    let isActive = true;

    const refreshIfNeeded = async () => {
      const cached = readOrderSnapshot(order.id);
      if (cached && Array.isArray(cached.apartments)) {
        hydrateApartmentState(cached);
      }

      try {
        const [aptsResult, attendedCount] = await Promise.all([
          getApartmentsByOrder(order.id),
          getAttendedApartments(order.id)
        ]);

        const snapshot = {
          apartments: aptsResult.apartments,
          contractedUnits: aptsResult.contractedUnits,
          attendedCount,
          apartmentPests: aptsResult.apartmentPests || {},
          savedAt: Date.now(),
        };

        const currentCache = readOrderSnapshot(order.id);
        if (!isActive) return;

        if (!currentCache || hasApartmentSnapshotChanged(currentCache, snapshot)) {
          writeOrderSnapshot(order.id, snapshot);
          hydrateApartmentState(snapshot);
          setApartmentPests(snapshot.apartmentPests || {});
        }
      } catch (e) {
        if (!isActive) return;
        const cached = readOrderSnapshot(order.id);
        if (cached) {
          hydrateApartmentState(cached);
        }
      }
    };

    refreshIfNeeded();
    const intervalId = setInterval(refreshIfNeeded, 30000);

    return () => {
      isActive = false;
      clearInterval(intervalId);
    };
  }, [order?.id]);

  // Fallback visual si no hay order válido
  if (!order || !order.id) {
    return (
      <div style={{ padding: 32 }}>
        <h2 style={{ marginBottom: 16 }}>{lang === 'en' ? 'No order selected' : 'No se seleccionó una orden'}</h2>
        <div style={{ color: '#64748b', fontWeight: 600, fontSize: 18 }}>
          {lang === 'en' ? 'Please select a building to view its apartments.' : 'Por favor selecciona un edificio para ver sus apartamentos.'}
        </div>
      </div>
    );
  }
  
  // Layout base para evitar ruptura de diseño
  if (loading) {
    return (
      <div style={{ padding: 32 }}>
        <h2 style={{ marginBottom: 16 }}>{L.title} <br/>{order?.building?.name || order?.buildingName || order?.building || ''}</h2>
        <div style={{ color: '#64748b', fontWeight: 600, fontSize: 18 }}>
          {lang === 'en' ? 'Loading apartments...' : 'Cargando apartamentos...'}
        </div>
      </div>
    );
  }

  // Si hay error, mostrar mensaje pero mantener el layout y permitir reintentar
  if (error) {
    return (
      <div style={{ padding: 32 }}>
        {/* Botón regresar cuando hay error */}
        {onBack && (
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onBack}
              style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              {lang === 'en' ? 'Back' : 'Regresar'}
            </button>
          </div>
        )}
        <h2 style={{ marginBottom: 16 }}>{L.title} {order?.building?.name || order?.buildingName || order?.building || ''}</h2>
        <div style={{ color: 'red', marginBottom: 16 }}>{lang === 'en' ? 'Error loading apartments:' : 'Error al cargar apartamentos:'} {error}</div>
        <button onClick={fetchApartments} style={{ padding: '8px 20px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontWeight: 700, cursor: 'pointer' }}>
          {lang === 'en' ? 'Retry' : 'Reintentar'}
        </button>
      </div>
    );
  }

  // Si no hay apartamentos en la orden
  if (!apartments || !apartments.length) {
    return (
      <div style={{ padding: 32 }}>
        {/* Botón regresar cuando no hay apartamentos */}
        {onBack && (
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onBack}
              style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              {lang === 'en' ? 'Back' : 'Regresar'}
            </button>
          </div>
        )}
        <h2 style={{ marginBottom: 16 }}>{L.title} {order?.building?.name || order?.buildingName || order?.building || ''}</h2>
        <div style={{ color: '#64748b', fontWeight: 600, fontSize: 18 }}>
          {lang === 'en' ? 'No apartments assigned to this order.' : 'No hay apartamentos asignados en esta orden.'}
        </div>
      </div>
    );
  }

  // Filtrar apartamentos que ya están completos (done=true)
  const pendientes = (apartments || []).filter(ap => !ap.done);

  return (
    <div style={{ padding: 4, height: '80vh' }}>
      <h3 style={{ marginBottom: 0, paddingTop: 0, paddingBottom: 4 }}>
         <div>Order #{order?.orderNumber}</div>
         <div>{order?.building?.address || order?.address || order?.building || ''}</div>
      </h3>
      {contracted !== null && attended !== null && (
        <div style={{ marginBottom: 12, color: limiteAlcanzado ? 'red' : '#2563eb', fontWeight: 600 }}>
          {typeof contracted === 'number' &&
            <span style={{ marginLeft: 16, background: '#e0e7ff', color: '#3730a3', borderRadius: 8, padding: '4px 12px', fontWeight: 600, fontSize: 15 }}>
               {lang === 'en' ? 'Contracted units' : 'Unidades contratadas'}: {contracted}
            </span>
          }
         <span style={{ marginLeft: 16, background: '#e0e7ff', color: '#3730a3', borderRadius: 8, padding: '4px 12px', fontWeight: 600, fontSize: 15 }}>
            {L.attended}: {attended}
         </span>
         {limiteAlcanzado && <span style={{width: '90%', fontSize: '.8em'}}>{L.limit}</span>}
        </div>
      )}

      {/* Mostrar toggle para ocultar/mostrar pendientes si hay muchos */}
      {pendientes.length > 0 && (
        <div style={{ marginBottom: 16, maxHeight: mostrarPendientes ? '80%' : 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <button
              onClick={() => setMostrarPendientes(v => !v)}
              style={{ padding: '6px 18px', background: '#64748b', color: 'white', border: 'none', borderRadius: 4, fontWeight: 600 }}
            >
              {mostrarPendientes
                ? (lang === 'en' ? 'Hide apartment list' : 'Ocultar lista de apartamentos')
                : (lang === 'en' ? `Show apartment list (${pendientes.length})` : `Mostrar lista de apartamentos (${pendientes.length})`)
              }
            </button>
          </div>
          {mostrarPendientes && (
            <ul style={{ 
              listStyle: 'none', 
              padding: 0, 
              maxHeight: 460, 
              overflowY: 'auto', 
              marginBottom: '16px',
              WebkitOverflowScrolling: 'touch',
              overscrollBehaviorY: 'contain',
              touchAction: 'pan-y',
              border: '1px dotted #7598e6', 
              borderRadius: 4 
            }}>
              {pendientes.map(ap => (
                <li
                  key={ap.id}
                  onClick={() => {
                    onSelect(ap, ap.pests || apartmentPests[ap.id] || [])
                  }}
                  style={{ 
                    padding: 12, 
                    marginBottom: 8, 
                    background: '#f1f5f9', 
                    borderRadius: 4, 
                    cursor: 'pointer', 
                    fontWeight: 600 
                  }}>
                  <span style={{ minWidth: '50px', display: 'inline-block' }}>{ap.description || ap.id}</span>
                  {Array.isArray(apartmentPests[ap.id]) && apartmentPests[ap.id].length > 0 && (
                    <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 14, color: '#64748b', marginTop: 2 }}>
                      {/* {lang === 'en' ? 'Pests: ' : 'Plagas: '} */}
                      {apartmentPests[ap.id].map(p => p.name).join(', ')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Formulario de insumos por orden (OrderSupplyUsed) - SIEMPRE MONTADO, OCULTO CON CSS SI NO limiteAlcanzado */}
      <div
        style={{
          maxWidth: 500,
          margin: '0 auto',
          marginTop: 32,
          marginBottom: 32,
          background: '#f8fafc',
          borderRadius: 8,
          padding: 16,
          border: '1px solid #e5e7eb',
          display: limiteAlcanzado ? undefined : 'none'
        }}
      >
        <b style={{ fontSize: 17, color: '#2563eb' }}>{lang === 'en' ? 'Supplies Used ' : 'Insumos utilizados '}</b>
        {orderSupplies.map((s, idx) => {
          const supply = suppliesList.find(x => String(x.id) === String(s.supplyId));
          return (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select
              value={s.supplyId ? String(s.supplyId) : ''}
              onChange={e => handleOrderSupplyChange(idx, 'supplyId', e.target.value)}
              style={{ flex: 2, padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
            >
              <option value="">{lang === 'en' ? 'Select supply' : 'Selecciona insumo'}</option>
              {suppliesList.map(sup => (
                <option key={sup.id} value={String(sup.id)}>{sup.name}{sup.outputUnit ? ` - ${sup.outputUnit}` : ''}</option>
              ))}
            </select>
            <input
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.,]?[0-9]*"
              value={s.quantity === '' || s.quantity === null || s.quantity === undefined ? '' : String(s.quantity)}
              onChange={e => {
                const raw = e.target.value;
                if (raw === '') {
                  handleOrderSupplyChange(idx, 'quantity', '');
                  return;
                }
                const normalizedRaw = raw.replace(',', '.');
                if (/^\d*\.?\d*$/.test(normalizedRaw) || /^\d*\.\d*$/.test(normalizedRaw)) {
                  handleOrderSupplyChange(idx, 'quantity', normalizedRaw);
                }
              }}
              style={{ width: 60, padding: 6, borderRadius: 4, border: '1px solid #ccc' }}
            />
            {orderSupplies.length > 1 && (
              <button type="button" onClick={() => removeOrderSupply(idx)} style={{ color: 'red', border: 'none', background: 'none', fontWeight: 700 }}>&times;</button>
            )}
            </div>
          );
        })}
        <button type="button" onClick={addOrderSupply} style={{ marginTop: 4, background: '#e0e7ff', border: 'none', borderRadius: 4, padding: '4px 12px', fontWeight: 700 }}>+ {lang === 'en' ? 'Add supply' : 'Agregar insumo'}</button>
        {/* El guardado de insumos ahora se realiza al pulsar Done */}
        {suppliesSuccess && <div style={{ color: '#16a34a', fontWeight: 700, marginTop: 6 }}>{suppliesSuccess}</div>}
        {suppliesError && <div style={{ color: 'red', fontWeight: 700, marginTop: 6 }}>{suppliesError}</div>}
      </div>

      {/* Botón para firmar: solo visible si se alcanzó el límite */}
        <button
          style={{ width: 200, margin: 8, padding: '10px 24px', background: firmaManager ? '#d1fae5' : '#22c55e', color: 'white', border: 'none', borderRadius: 4, fontWeight: 700 }}
          onClick={() => setShowSignature(true)}
          disabled={firmaManager}
        >
          {firmaManager ? (lang === 'en' ? 'Signed' : 'Firmado') : (lang === 'en' ? 'Sign' : 'Firmar')}
        </button>

      {/* Modal de firma */}
      {showSignature && !firmaManager && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#d1f2fc', borderRadius: 12, padding: 24, boxShadow: '0 2px 16px #0003', minWidth: 360 }}>
            <h3 style={{ marginBottom: 12 }}>{lang === 'en' ? 'Sign here' : 'Firme aquí'}</h3>
            <SignatureCapture 
              onSave={async (dataUrl) => {
              setSignatureData(dataUrl);
              setFirmaManager(true);
              setShowSignature(false);
              // Guardar firma en backend
              const token = localStorage.getItem('jwt_token');
              try {
                const resp = await fetch(`${API_URL}/confirmations`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ orderId: order.id, signature: dataUrl })
                });
                if (!resp.ok) throw new Error(lang === 'en' ? 'Error registering signature.' : 'No se pudo registrar la firma');
                setFirmaManager(true);
                // alert(lang === 'en' ? 'Signature registered successfully.' : 'Signature registered Successfully.');
              } catch (err) {
                alert(lang === 'en' ? 'Error registering signature.' : 'Error registering signature.');
              }
            }}
              clearLabel={lang === 'en' ? 'Clear' : 'Limpiar'}
              saveLabel={lang === 'en' ? 'Save signature' : 'Guardar firma'}
            />
            <button onClick={
              () => setShowSignature(false)} 
              style={{ 
                marginTop: 16, 
                background: '#64748b', 
                color: 'white', 
                border: 'none', 
                borderRadius: 4, 
                padding: '8px 20px', fontWeight: 600 
              }}>
              {lang === 'en' ? 'Cancel' : 'Cancelar'}
            </button>
          </div>
        </div>
      )}

      { limiteAlcanzado && ( 
        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
            {/* Botón Done para completar la orden */}
            <button
              style={{
                width: 200,
                margin: 8,
                padding: '10px 24px',
                background: completingOrder ? '#6b7280' : '#059669',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontWeight: 700,
                opacity: completingOrder ? 0.7 : 1,
                cursor: completingOrder ? 'not-allowed' : 'pointer'
              }}
              onClick={async () => {
                if (completingOrder) return;

                setCompletingOrder(true);

                try {
                  // Guardar insumos y luego cerrar la orden
                  const validSupplies = getValidSupplyPayload(orderSupplies);
                  if (validSupplies.length === 0) {
                    alert(lang === 'en' ? 'Add at least one supply before completing the order.' : 'Agrega al menos un insumo antes de completar la orden.');
                    return;
                  }

                  const jwt = localStorage.getItem('jwt_token');
                  const respSupplies = await fetch(`${API_URL}/orders/${order.id}/supplies-used`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': jwt ? `Bearer ${jwt}` : ''
                    },
                    body: JSON.stringify(validSupplies)
                  });
                  if (!respSupplies.ok) throw new Error(lang === 'en' ? 'Error saving supplies.' : 'Error al guardar insumos.');

                  const resp = await fetch(`${API_URL}/orders/${order.id}/complete`, {
                    method: 'PATCH',
                    headers: {
                      'Authorization': `Bearer ${getToken()}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  if (!resp.ok) {
                    const contentType = resp.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                      const error = await resp.json();
                      throw new Error(error.error || 'Failed to update order');
                    } else {
                      const text = await resp.text();
                      console.error('Non-JSON response:', text);
                      throw new Error('Server error: ' + resp.status);
                    }
                  }

                  if (typeof onOrderCompleted === 'function') {
                    onOrderCompleted();
                    localStorage.removeItem(`pwa_order_signature_${order.id}`);
                    setSignatureData(null);
                    setFirmaManager(false);
                  } else if (typeof onAllAttended === 'function') {
                    onAllAttended(order);
                  }
                } catch (err) {
                  console.error('Error completing order:', err);
                  alert((lang === 'en' ? 'Error completing order: ' : 'Error al completar la orden: ') + (err.message || 'Unknown error'));
                } finally {
                  setCompletingOrder(false);
                }
              }}
              disabled={completingOrder}
            >
              {completingOrder ? (lang === 'en' ? 'Processing...' : 'Procesando...') : (lang === 'en' ? 'Done' : 'Finalizar')}
            </button>
            
          </div>
        </div>
      )}

      {/* Vista temporal de parámetros recuperados */}
      {settings && (
        <div style={{ marginTop: 32, background: '#f3f4f6', borderRadius: 8, padding: 16, fontSize: 15, color: '#334155' }}>
          <div style={{ fontWeight: 700, marginBottom: 8, color: '#2563eb' }}>
            {lang === 'en' ? 'Company Settings (DEBUG)' : 'Parámetros de empresa (DEPURACIÓN)'}
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{JSON.stringify(settings, null, 2)}</pre>
          <div style={{ marginTop: 8, color: '#64748b', fontSize: 13 }}>
            {lang === 'en' ? 'If you see this box, settings are being rendered.' : 'Si ves este recuadro, los parámetros se están mostrando.'}
          </div>
        </div>
      )}
    </div>
  );
}
